import React, { useState, useEffect } from 'react';
import Breadcrumb from '../components/breadcrumb';
import { CheckCircle, RotateCw, MinusCircle } from 'lucide-react';
import Plot from 'react-plotly.js';
import { useNavigate } from 'react-router-dom';
import { JobsiteService, Jobsite, WorkersPercentageResponse } from '../services/jobsite.service';
import { toast } from 'react-toastify';
import JobsiteCard from '../components/JobsiteCard';
import {
    StatCardSkeleton,
    JobsiteCardSkeleton,
    CompletionStagesSkeleton,
    WorkersPercentageSkeleton,
    FundingSourcesSkeleton,
    ComparisonChartSkeleton
} from '../components/Skeleton';
import { DashboardService } from '../services/dashboard.service';
import { getJobsiteCompletionPercentage } from '../utils/jobsiteUtils';

interface CountPercentage {
    count: number;
    percentage: number;
}

interface Industry {
    name: string;
    union: CountPercentage;
    mixed: CountPercentage;
    nonUnion: CountPercentage;
}

interface ComparisonResponse {
    sector: string;
    industries?: Industry[];
    union?: CountPercentage;
    mixed?: CountPercentage;
    nonUnion?: CountPercentage;
}

interface ServiceIndustry {
    name: string;
    union: { count: number; percentage: number };
    mixed: { count: number; percentage: number };
    nonUnion: { count: number; percentage: number };
}

interface ServiceComparisonResponse {
    sector: string;
    industries?: ServiceIndustry[];
    union?: { count: number; percentage: number };
    mixed?: { count: number; percentage: number };
    nonUnion?: { count: number; percentage: number };
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [jobSites, setJobSites] = useState<Jobsite[]>([]);
    const [jobsiteCount, setJobsiteCount] = useState<{
        totalJobsites: number;
        completed: number;
        inProgress: number;
        inactiveJobSiteCount?: number;
      } | null>(null);
    const [fundingResources, setFundingResources] = useState<{ name: string; count: number; percentage: number }[]>([]);
    const [workersPercentageData, setWorkersPercentageData] = useState<WorkersPercentageResponse[]>([]);
    const [completionStages, setCompletionStages] = useState<any[]>([]);
    const [comparisonData, setComparisonData] = useState<ComparisonResponse[]>([]);
    const [workerTypeStats, setWorkerTypeStats] = useState<{ name: string; count: number; percentage: number }[]>([]);
    const [isLoadingWorkerTypeStats, setIsLoadingWorkerTypeStats] = useState(true);

    // State to manage the selected time range
    const [selectedRange, setSelectedRange] = useState<'thisMonth' | 'sixMonths' | 'thisYear'>('thisYear');

    // Add loading states
    const [isLoadingJobsites, setIsLoadingJobsites] = useState(true);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [isLoadingFunding, setIsLoadingFunding] = useState(true);
    const [isLoadingWorkers, setIsLoadingWorkers] = useState(true);
    const [isLoadingStages, setIsLoadingStages] = useState(true);
    const [isLoadingComparison, setIsLoadingComparison] = useState(true);

    // Add helper function to check if all values are zero
    const hasData = (data: any) => {
        if (!data) return false;
        
        if (Array.isArray(data)) {
            return data.some(item => {
                if (item.union && item.mixed && item.nonUnion) {
                    return (
                        item.union.count > 0 || 
                        item.mixed.count > 0 || 
                        item.nonUnion.count > 0
                    );
                }
                return false;
            });
        }
        
        return (
            (data.union?.count > 0) || 
            (data.mixed?.count > 0) || 
            (data.nonUnion?.count > 0)
        );
    };

    // Helper function to convert range to API parameter
    const getTimeRangeParam = (range: 'thisMonth' | 'sixMonths' | 'thisYear'): string => {
        switch (range) {
            case 'thisMonth':
                return 'thismonth';
            case 'sixMonths':
                return 'sixMonths';
            case 'thisYear':
                return 'thisYear';
            default:
                return 'thismonth';
        }
    };

    // Update useEffect for jobsites to handle loading state
    useEffect(() => {
        const fetchJobsites = async () => {
            setIsLoadingJobsites(true);
            try {
                const response = await JobsiteService.getJobsites({
                    limit: '6',
                    page: '1'
                });
                setJobSites(response.data);
            } catch (error) {
                console.error('Error fetching jobsites:', error);
                toast.error('Failed to fetch recent jobsites');
            } finally {
                setIsLoadingJobsites(false);
            }
        };

        fetchJobsites();
    }, []);

    useEffect(() => {
        const fetchJobsites = async (limit: number) => {
            try {
                await JobsiteService.getJobsites({
                    limit: `${limit}`,
                    page: '1'
                });
            } catch (error) {
                console.error('Error fetching jobsites:', error);
                toast.error('Failed to fetch jobsites');
            }
        };

        fetchJobsites(10);
    }, []);

    // Update useEffect for funding resources to handle loading state
    useEffect(() => {
        const fetchFundingResources = async () => {
            setIsLoadingFunding(true);
            try {
                const timeRange = getTimeRangeParam(selectedRange);
                const fundingData = await JobsiteService.fundingResources(timeRange);
                setFundingResources(fundingData.resources);
            } catch (error) {
                console.error('Error fetching funding resources:', error);
                toast.error('Failed to fetch funding resources');
            } finally {
                setIsLoadingFunding(false);
            }
        };

        fetchFundingResources();
    }, [selectedRange]);

    // Update useEffect for jobsite count to handle loading state
    useEffect(() => {
        const fetchJobsiteCount = async () => {
            setIsLoadingStats(true);
            try {
                const timeRange = getTimeRangeParam(selectedRange);
                const countData = await JobsiteService.getJobsiteCount(timeRange);
                setJobsiteCount(countData);
            } catch (error) {
                console.error('Error fetching jobsite count:', error);
                toast.error('Failed to fetch jobsite count');
            } finally {
                setIsLoadingStats(false);
            }
        };

        fetchJobsiteCount();
    }, [selectedRange]);

    // Update useEffect for workers percentage to handle loading state
    useEffect(() => {
        const fetchWorkersPercentage = async () => {
            setIsLoadingWorkers(true);
            try {
                const timeRange = getTimeRangeParam(selectedRange);
                const response = await JobsiteService.getWorkersPercentage(timeRange) as unknown as WorkersPercentageResponse[];
                setWorkersPercentageData(response);
            } catch (error) {
                console.error('Error fetching workers percentage:', error);
                toast.error('Failed to fetch workers percentage');
            } finally {
                setIsLoadingWorkers(false);
            }
        };

        fetchWorkersPercentage();
    }, [selectedRange]);

    // Update useEffect for completion stages to handle loading state
    useEffect(() => {
        const fetchCompletionStages = async () => {
            setIsLoadingStages(true);
            try {
                const timeRange = getTimeRangeParam(selectedRange);
                const stagesData = await JobsiteService.getCompletionStages(timeRange);
                if (Array.isArray(stagesData)) {
                    setCompletionStages(stagesData);
                }
            } catch (error) {
                console.error('Error fetching completion stages:', error);
            } finally {
                setIsLoadingStages(false);
            }
        };

        fetchCompletionStages();
    }, [selectedRange]);

    // Update useEffect for comparison data to handle loading state
    useEffect(() => {
        const fetchComparisonData = async () => {
            setIsLoadingComparison(true);
            try {
                const timeRange = getTimeRangeParam(selectedRange);
                const response = await JobsiteService.getComparison(timeRange);
                if (Array.isArray(response)) {
                    // Transform the data to match our interface
                    const transformedData = response.map((item: ServiceComparisonResponse) => {
                        if (item.industries) {
                            return {
                                ...item,
                                industries: item.industries.map((industry: ServiceIndustry) => ({
                                    name: industry.name,
                                    union: {
                                        count: industry.union.count,
                                        percentage: industry.union.percentage
                                    },
                                    mixed: {
                                        count: industry.mixed.count,
                                        percentage: industry.mixed.percentage
                                    },
                                    nonUnion: {
                                        count: industry.nonUnion.count,
                                        percentage: industry.nonUnion.percentage
                                    }
                                }))
                            };
                        }
                        return {
                            ...item,
                            union: item.union ? {
                                count: item.union.count,
                                percentage: item.union.percentage
                            } : undefined,
                            mixed: item.mixed ? {
                                count: item.mixed.count,
                                percentage: item.mixed.percentage
                            } : undefined,
                            nonUnion: item.nonUnion ? {
                                count: item.nonUnion.count,
                                percentage: item.nonUnion.percentage
                            } : undefined
                        };
                    });
                    setComparisonData(transformedData as ComparisonResponse[]);
                } else {
                    setComparisonData([response] as ComparisonResponse[]);
                }
            } catch (error) {
                console.error('Error fetching comparison data:', error);
                toast.error('Failed to fetch comparison data');
            } finally {
                setIsLoadingComparison(false);
            }
        };

        fetchComparisonData();
    }, [selectedRange]);


    // Add useEffect for worker type statistics
    useEffect(() => {
        const fetchWorkerTypeStats = async () => {
            setIsLoadingWorkerTypeStats(true);
            try {
                const timeRange = getTimeRangeParam(selectedRange);
                const stats = await DashboardService.getWorkerTypeStatistics(timeRange);
                setWorkerTypeStats(stats.resources);
            } catch (error) {
                console.error('Error fetching worker type statistics:', error);
                toast.error('Failed to fetch worker type statistics');
            } finally {
                setIsLoadingWorkerTypeStats(false);
            }
        };
        fetchWorkerTypeStats();
    }, [selectedRange]);

    // Handler for time range selection
    const handleRangeSelect = (range: 'thisMonth' | 'sixMonths' | 'thisYear') => {
        setSelectedRange(range);
        // No need for additional logic here as useEffects will handle the data fetching
    };

    // Add handler for jobsite click
    const handleJobsiteClick = (siteId?: string) => {
        navigate(`/jobsite-management/details/${siteId || '0'}`);
    };

    // Define the stage headers based on the API response structure
    const stageHeaders = [
        'Pre-construction', 'Site Services',
        'Building Shell', 'Rough-In', 'Finish Work', 'Completed'
    ];

    // Function to determine the class based on percentage
    const getCompletionClass = (stageData: { count: number; percentage: number }): string => {
        if (stageData.count === 0) return 'bg-gray-100'; // No jobs
        if (stageData.count <= 6) return 'bg-green-500'; // 1-6 jobs
        if (stageData.count <= 12) return 'bg-orange-500'; // 7-12 jobs
        return 'bg-red-600'; // More than 12 jobs
    };

    return (
        <div className="bg-gray-50 min-h-screen p-3 sm:p-4 md:p-6 mb-4  " id="dashboard-container">
            <div className="mb-4 sm:mb-6 flex flex-col md:flex-row md:items-center">
                <div className="flex-1">
                    <Breadcrumb title="Analytical Dashboard" />
                </div>
                <div className="flex border border-[#E8E8E8] items-center bg-gray-100 rounded-lg p-1 w-full mt-2 md:mt-0 md:w-fit whitespace-nowrap">
                    {['thisMonth', 'sixMonths', 'thisYear'].map((range) => (
                        <button
                            key={range}
                            onClick={() => handleRangeSelect(range as 'thisMonth' | 'sixMonths' | 'thisYear')}
                            className={`flex-1 px-3 py-2 text-[16px] font-medium rounded-md transition h-[35px] ${selectedRange === range ? 'bg-white shadow text-black' : 'text-[#55575B]'}`}
                        >
                            {range === 'thisMonth' ? 'This Month' : range === 'sixMonths' ? '6 Months' : 'This Year'}
                        </button>
                    ))}
                </div>
            </div>

            <div className="overflow-hidden">
                <div className="flex flex-col 2xl:flex-row 2xl:space-x-4 space-y-4 2xl:space-y-0">
                    {/* Left section (2/3 width) */}
                    <div className="w-full 2xl:w-2/3 flex flex-col space-y-4">
                        {/* Jobsites Completion Stages */}
                        {isLoadingStages ? (
                            <CompletionStagesSkeleton />
                        ) : (
                            <div className="bg-white rounded-md border p-2 md:p-4 flex-none min-h-[400px]">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-[20px] font-[700] text-[#000000]">Jobsites by completion stages</h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <div className="overflow-y-auto">
                                        {completionStages.length > 0 ? (
                                            <>
                                                <div className="grid grid-cols-7 gap-2 mb-4 sticky top-0 bg-white z-6">
                                                    <div className="col-span-1 font-medium"></div>
                                                    {stageHeaders.map(header => (
                                                        <div key={header} className="text-[14px] font-[500] text-[#767A81] text-center">
                                                            {header}
                                                        </div>
                                                    ))}
                                                </div>
                                                {completionStages.map((row, index) => (
                                                    <div key={index} className="grid grid-cols-7 gap-2 mb-2">
                                                        <div className="flex items-center text-[14px] font-[500] text-[#767A81] sticky left-0 bg-white whitespace-nowrap truncate" title= {row.sector === 'Residential Housing/Single Family Dwelling' ? 'Residential Housing' : row.sector}>
                                                            {row.sector === 'Residential Housing/Single Family Dwelling' ? 'Residential Housing' : row.sector}
                                                        </div>
                                                        <div className={`text-center p-1 text-sm h-8 flex items-center justify-center ${getCompletionClass(row.stages.preConstruction)}`}>
                                                            {row.stages.preConstruction.count}
                                                        </div>
                                                        <div className={`text-center p-1 text-sm h-8 flex items-center justify-center ${getCompletionClass(row.stages.siteServices)}`}>
                                                            {row.stages.siteServices.count}
                                                        </div>
                                                        <div className={`text-center p-1 text-sm h-8 flex items-center justify-center ${getCompletionClass(row.stages.buildingShell)}`}>
                                                            {row.stages.buildingShell.count}
                                                        </div>
                                                        <div className={`text-center p-1 text-sm h-8 flex items-center justify-center ${getCompletionClass(row.stages.roughIn)}`}>
                                                            {row.stages.roughIn.count}
                                                        </div>
                                                        <div className={`text-center p-1 text-sm h-8 flex items-center justify-center ${getCompletionClass(row.stages.finishWork)}`}>
                                                            {row.stages.finishWork.count}
                                                        </div>
                                                        <div className={`text-center p-1 text-sm h-8 flex items-center justify-center ${getCompletionClass(row.stages.projectCompleted)}`}>
                                                            {row.stages.projectCompleted.count}
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                                    <svg className="w-10 h-10 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                                    <p className="text-base font-medium text-gray-500">No data available</p>
                                                    <p className="text-sm text-gray-400 mt-1">Try selecting a different time range</p>
                                                </div>
                                        )}
                                    </div>
                                  
                                </div>
                                <div className='flex justify-between items-center'>
                                <div className="mt-4 text-xs text-gray-500 text-right">
                                        <span>* Numbers represent jobsites count at each stage</span>
                                    </div>
                                {/* Color legend for jobsite counts */}
                                <div className="mt-2 flex flex-wrap gap-4 justify-end text-xs">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-4 h-4 rounded bg-gray-100 border border-gray-300"></span>
                                        <span className="text-gray-700">0 jobsites</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-4 h-4 rounded bg-green-500"></span>
                                        <span className="text-gray-700">1-6 jobsites</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-4 h-4 rounded bg-orange-500"></span>
                                        <span className="text-gray-700">7-12 jobsites</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-4 h-4 rounded bg-red-600"></span>
                                        <span className="text-gray-700">13+ jobsites</span>
                                    </div>
                                </div>
                                </div>
                            </div>
                        )}

                        {/* Bottom cards container */}
                        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 flex-1">
                            {/* Left column */}
                            <div className="w-full md:w-1/2 flex flex-col space-y-4">
                                {/* Funding Sources */}
                                {isLoadingFunding ? (
                                    <FundingSourcesSkeleton />
                                ) : (
                                    <div className="bg-white rounded-md border p-4 flex-1 min-h-[400px]">
                                        <h2 className="text-[20px] font-[700] text-[#000000] mb-4">Funding Sources</h2>
                                        {fundingResources.some(r => r.count > 0) ? (
                                            <Plot
                                                data={[
                                                    {
                                                        values: fundingResources.map(resource => resource.percentage),
                                                        labels: fundingResources.map(resource => resource.name),
                                                        type: 'pie',
                                                        hole: 0.7,
                                                        marker: {
                                                            colors: ['#A42823', '#E8E8E8', '#541513', '#767A81']
                                                        },
                                                        textinfo: 'none',
                                                        hoverinfo: 'label+text',
                                                        text: fundingResources.map(resource => `${resource.percentage}%`),
                                                        hovertemplate: '%{label}<br>%{text}<extra></extra>'
                                                    }
                                                ]}
                                                layout={{
                                                    showlegend: true,
                                                    legend: {
                                                        orientation: 'h',
                                                        yanchor: 'bottom',
                                                        y: -0.3,
                                                        xanchor: 'center',
                                                        x: 0.5,
                                                        font: {
                                                            size: 14
                                                        }
                                                    },
                                                    margin: {
                                                        l: 20,
                                                        r: 20,
                                                        t: 20,
                                                        b: 60
                                                    },
                                                    width: 350,
                                                    height: 300,
                                                    annotations: [
                                                        {
                                                            text: 'Funding<br>Sources',
                                                            showarrow: false,
                                                            font: {
                                                                size: 16,
                                                                color: '#000000'
                                                            },
                                                            x: 0.5,
                                                            y: 0.5
                                                        }
                                                    ]
                                                }}
                                                config={{
                                                    displayModeBar: false,
                                                    responsive: true
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                                useResizeHandler={true}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-[calc(100%-3rem)] text-center">
                                                <svg className="w-10 h-10 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                                <p className="text-base font-medium text-gray-500">No data available</p>
                                                <p className="text-sm text-gray-400 mt-1">Try selecting a different time range</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Worker Type Statistics Chart */}
                                {isLoadingWorkerTypeStats ? (
                                    <FundingSourcesSkeleton />
                                ) : (
                                    <div className="bg-white rounded-md border p-4 flex-1 min-h-[400px] mt-4">
                                        <h2 className="text-[20px] font-[700] text-[#000000] mb-4">Worker Type Statistics</h2>
                                        {workerTypeStats.some(r => r.count > 0) ? (
                                            <Plot
                                                data={[
                                                    {
                                                        values: workerTypeStats.map(resource => resource.percentage),
                                                        labels: workerTypeStats.map(resource => resource.name === 'non-union' ? 'Non-Union' : resource.name.charAt(0).toUpperCase() + resource.name.slice(1)),
                                                        type: 'pie',
                                                        hole: 0.7,
                                                        marker: {
                                                            colors: ['#A42823', '#E8E8E8', '#541513', '#767A81']
                                                        },
                                                        textinfo: 'none',
                                                        hoverinfo: 'label+text',
                                                        text: workerTypeStats.map(resource => `${resource.percentage}%`),
                                                        hovertemplate: '%{label}<br>%{text}<extra></extra>'
                                                    }
                                                ]}
                                                layout={{
                                                    showlegend: true,
                                                    legend: {
                                                        orientation: 'h',
                                                        yanchor: 'bottom',
                                                        y: -0.3,
                                                        xanchor: 'center',
                                                        x: 0.5,
                                                        font: {
                                                            size: 14
                                                        }
                                                    },
                                                    margin: {
                                                        l: 20,
                                                        r: 20,
                                                        t: 20,
                                                        b: 60
                                                    },
                                                    width: 350,
                                                    height: 300,
                                                    annotations: [
                                                        {
                                                            text: 'Worker Type',
                                                            showarrow: false,
                                                            font: {
                                                                size: 16,
                                                                color: '#000000'
                                                            },
                                                            x: 0.5,
                                                            y: 0.5
                                                        }
                                                    ]
                                                }}
                                                config={{
                                                    displayModeBar: false,
                                                    responsive: true
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                                useResizeHandler={true}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-[calc(100%-3rem)] text-center">
                                                <svg className="w-10 h-10 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                                <p className="text-base font-medium text-gray-500">No data available</p>
                                                <p className="text-sm text-gray-400 mt-1">Try selecting a different time range</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                

                            </div>

                            {/* Right column */}
                            <div className="w-full md:w-1/2 flex flex-col space-y-4">
                                {/* Worker Affiliation by Province */}
                                {isLoadingWorkers ? (
                                    <WorkersPercentageSkeleton />
                                ) : workersPercentageData.length > 0 ? (
                                    <div className="bg-white rounded-md border p-4 h-full">
                                        <h2 className="text-[20px] font-[700] text-[#000000] mb-4">Worker Affiliation by Province</h2>
                                        <div className="space-y-2 max-h-[calc(100%-2rem)] overflow-y-auto">
                                            {workersPercentageData.map(region => (
                                                <div key={region.province} className="flex items-center space-y-2" >
                                                    <div className="flex justify-between text-[14px] font-[500] text-[#767A81] w-full">
                                                        <span>{region.province}</span>
                                                    </div>
                                                    {region.totalWorkers === 0 ? (
                                                        <div className="flex h-4 w-full md:mr-4 rounded border border-dashed border-gray-300 items-center justify-center">
                                                            <span className="text-[10px] text-gray-400">No data</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex h-4 bg-gray-200 rounded w-full md:mr-4 cursor-pointer">
                                                            <div
                                                                title={`Union: ${region.percentages.union}%`}
                                                                className={`bg-[#A42823] rounded-l ${region.percentages.mixed === 0 && region.percentages.nonUnion === 0 ? 'rounded-r' : ''}`}
                                                                style={{ width: `${region.percentages.union}%` }}
                                                            ></div>
                                                            <div
                                                                title={`Mixed: ${region.percentages.mixed}%`}
                                                                className={`bg-[#541513] ${region.percentages.nonUnion === 0 ? 'rounded-r' : ''} ${region.percentages.union === 0 ? 'rounded-l' : ''}`}
                                                                style={{ width: `${region.percentages.mixed}%` }}
                                                            ></div>
                                                            <div
                                                                title={`Non-Union: ${region.percentages.nonUnion}%`}
                                                                className={`bg-[#767A81] rounded-r ${region.percentages.union === 0 && region.percentages.mixed === 0 ? 'rounded-l' : ''}`}
                                                                style={{ width: `${region.percentages.nonUnion}%` }}
                                                            ></div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex gap-4 mt-4 text-sm">
                                            <span className="flex items-center gap-1">
                                                <div className="w-3 h-3 bg-[#A42823]"></div> Union
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <div className="w-3 h-3 bg-[#541513]"></div> Mixed
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <div className="w-3 h-3 bg-[#767A81]"></div> Non-Union
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-md border p-4 h-full flex items-center justify-center">
                                        <div className="flex flex-col items-center justify-center text-center">
                                            <svg className="w-10 h-10 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                            <p className="text-base font-medium text-gray-500">No data available</p>
                                            <p className="text-sm text-gray-400 mt-1">Try selecting a different time range</p>
                                        </div>
                                    </div>
                                )}
                                {/* Total Jobsites Card */}
                                {isLoadingStats ? (
                                    <StatCardSkeleton />
                                ) : (
                                    <div className="bg-white rounded-md border p-4 flex-none min-h-[180px]">
                                        <h2 className="text-[20px] font-[700] text-[#000000] mb-2">Total Jobsites</h2>
                                        <div className="flex items-center mb-4">
                                            <span className="text-4xl font-bold">{jobsiteCount ? jobsiteCount.totalJobsites : '0'}</span>
                                            <span className="ml-2 text-green-500 text-xl">▲</span>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-2">
                                                    <span className="w-7 h-7 rounded-full flex items-center justify-center bg-yellow-400">
                                                        <RotateCw className="text-black w-5 h-5" strokeWidth={2.2} />
                                                    </span>
                                                    <span className="text-[16px]">In-Progress</span>
                                                </span>
                                                <span className="text-[16px] font-bold">{jobsiteCount ? jobsiteCount.inProgress : '0'}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-2">
                                                    {/* Green check icon */}
                                                    <span className="w-7 h-7 rounded-full flex items-center justify-center bg-green-500">
                                                        <CheckCircle className="text-white" size={18} strokeWidth={2.2} />
                                                    </span>
                                                    <span className="text-[16px]">Completed</span>
                                                </span>
                                                <span className="text-[16px] font-bold">{jobsiteCount ? jobsiteCount.completed : '0'}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-2">
                                                    {/* Lucide MinusCircle icon for Inactive */}
                                                    <span className="w-7 h-7 rounded-full flex items-center justify-center bg-white">
                                                        <MinusCircle className="text-red-600" size={28} strokeWidth={2.5} />
                                                    </span>
                                                    <span className="text-[16px]">Inactive</span>
                                                </span>
                                                <span className="text-[16px] font-bold">{jobsiteCount ? jobsiteCount.inactiveJobSiteCount : '0'}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                        </div>
                    </div>

                    {/* Right section (1/3 width) */}
                    <div className="w-full 2xl:w-1/3">
                        <div className="bg-white rounded-md border p-4 h-full min-h-[800px]">
                            <h2 className="text-[20px] font-[700] text-[#000000] mb-4">Recently added Jobsites</h2>
                            <div className="space-y-6 overflow-y-auto max-h-[calc(100%-3rem)] rounded-md scrollbar-hide">
                                {isLoadingJobsites ? (
                                    [...Array(6)].map((_, index) => (
                                        <JobsiteCardSkeleton key={index} />
                                    ))
                                ) : jobSites.length > 0 ? (
                                    jobSites.map((site) => (
                                        <JobsiteCard
                                            key={site.id}
                                            site={site}
                                            onClick={() => handleJobsiteClick(site.id)}
                                            onNameClick={() => handleJobsiteClick(site.id)}
                                            getCompletionPercentage={getJobsiteCompletionPercentage}
                                            showMapButton={false}
                                        />
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-500 p-4 text-center">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <p className="text-xl font-medium mb-1">No Jobsites Yet!</p>
                                        <p className="text-sm text-gray-400">Add a jobsite to see it here</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4 bg-white rounded-md border p-4 h-auto">
                    <h2 className="text-[20px] font-[700] text-[#000000] mb-4">Union, Non-Union, Mixed Jobsite comparison by Sector and Industry</h2>


                    {/* Legend */}
                    <div className="flex justify-center gap-6 mt-6 mb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-[#A42823]"></div>
                            <span>Union</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-[#541513]"></div>
                            <span>Mixed</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-[#767A81]"></div>
                            <span>Non-Union</span>
                        </div>
                    </div>

                    <div className="gap-6">
                        {!isLoadingComparison && comparisonData.length > 0 && !comparisonData.some(item =>
                            hasData(item.industries || []) || hasData(item)
                        ) && (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <svg className="w-10 h-10 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                <p className="text-base font-medium text-gray-500">No data available</p>
                                <p className="text-sm text-gray-400 mt-1">Try selecting a different time range</p>
                            </div>
                        )}
                        {/* Commercial Sector */}
                        {hasData(comparisonData.find(item => item.sector === "Commercial")?.industries || []) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Commercial</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const commercialData = comparisonData.find(item => item.sector === "Commercial")?.industries || [];
                                        if (commercialData.length === 0) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        const industryNames = commercialData.map(industry => industry.name);
                                        const unionValues = commercialData.map(industry => industry.union.count);
                                        const mixedValues = commercialData.map(industry => industry.mixed.count);
                                        const nonUnionValues = commercialData.map(industry => industry.nonUnion.count);
                                        
                                        const maxCommercialValue = Math.max(
                                            ...unionValues.map((v, i) => v + mixedValues[i] + nonUnionValues[i])
                                        );
                                        
                                        const commercialYAxisMax = maxCommercialValue > 0 ? Math.ceil(maxCommercialValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: industryNames,
                                                        y: unionValues,
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: commercialData.map(industry => (industry.union?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: industryNames,
                                                        y: mixedValues,
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: commercialData.map(industry => (industry.mixed?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: industryNames,
                                                        y: nonUnionValues,
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: commercialData.map(industry => (industry.nonUnion?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 80 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, commercialYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Institutional Sector */}
                        {hasData(comparisonData.find(item => item.sector === "Institutional")?.industries || []) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Institutional</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const institutionalData = comparisonData.find(item => item.sector === "Institutional")?.industries || [];
                                        if (institutionalData.length === 0) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        const industryNames = institutionalData.map(industry => industry.name);
                                        const unionValues = institutionalData.map(industry => industry.union.count);
                                        const mixedValues = institutionalData.map(industry => industry.mixed.count);
                                        const nonUnionValues = institutionalData.map(industry => industry.nonUnion.count);
                                        
                                        const maxInstitutionalValue = Math.max(
                                            ...unionValues.map((v, i) => v + mixedValues[i] + nonUnionValues[i])
                                        );
                                        
                                        const institutionalYAxisMax = maxInstitutionalValue > 0 ? Math.ceil(maxInstitutionalValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: industryNames,
                                                        y: unionValues,
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: institutionalData.map(industry => (industry.union?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: industryNames,
                                                        y: mixedValues,
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: institutionalData.map(industry => (industry.mixed?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: industryNames,
                                                        y: nonUnionValues,
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: institutionalData.map(industry => (industry.nonUnion?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 80 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, institutionalYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Industrial Sector */}
                        {hasData(comparisonData.find(item => item.sector === "Industrial")?.industries || []) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Industrial</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const industrialData = comparisonData.find(item => item.sector === "Industrial")?.industries || [];
                                        if (industrialData.length === 0) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        const industryNames = industrialData.map(industry => industry.name);
                                        const unionValues = industrialData.map(industry => industry.union.count);
                                        const mixedValues = industrialData.map(industry => industry.mixed.count);
                                        const nonUnionValues = industrialData.map(industry => industry.nonUnion.count);
                                        
                                        const maxValue = Math.max(
                                            ...unionValues.map((v, i) => v + mixedValues[i] + nonUnionValues[i])
                                        );
                                        
                                        const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: industryNames,
                                                        y: unionValues,
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: industrialData.map(industry => (industry.union?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: industryNames,
                                                        y: mixedValues,
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: industrialData.map(industry => (industry.mixed?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: industryNames,
                                                        y: nonUnionValues,
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.25,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: industrialData.map(industry => (industry.nonUnion?.percentage || 0).toFixed(1)),
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 80 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, yAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}


<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Residential Housing */}
                        {hasData(comparisonData.find(item => item.sector === "Residential Housing/Single Family Dwelling")) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Residential Housing</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const residentialData = comparisonData.find(item => item.sector === "Residential Housing/Single Family Dwelling");
                                        if (!residentialData) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        
                                        const unionValue = residentialData?.union?.count ?? 0;
                                        const mixedValue = residentialData?.mixed?.count ?? 0;
                                        const nonUnionValue = residentialData?.nonUnion?.count ?? 0;
                                        
                                        const totalValue = unionValue + mixedValue + nonUnionValue;
                                        const residentialYAxisMax = totalValue > 0 ? Math.ceil(totalValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: ['Residential Housing'],
                                                        y: [unionValue],
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(residentialData.union?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Residential Housing'],
                                                        y: [mixedValue],
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(residentialData.mixed?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Residential Housing'],
                                                        y: [nonUnionValue],
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(residentialData.nonUnion?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 40 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, residentialYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Highrise Residential */}
                        {hasData(comparisonData.find(item => item.sector === "Highrise Residential")) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Highrise Residential</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const highriseData = comparisonData.find(item => item.sector === "Highrise Residential");
                                        if (!highriseData) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        
                                        const unionValue = highriseData?.union?.count ?? 0;
                                        const mixedValue = highriseData?.mixed?.count ?? 0;
                                        const nonUnionValue = highriseData?.nonUnion?.count ?? 0;
                                        
                                        const totalValue = unionValue + mixedValue + nonUnionValue;
                                        const highriseYAxisMax = totalValue > 0 ? Math.ceil(totalValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: ['Highrise Residential'],
                                                        y: [unionValue],
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(highriseData.union?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Highrise Residential'],
                                                        y: [mixedValue],
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(highriseData.mixed?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Highrise Residential'],
                                                        y: [nonUnionValue],
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(highriseData.nonUnion?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 40 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, highriseYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Service */}
                        {hasData(comparisonData.find(item => item.sector === "Service")) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Service</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const serviceData = comparisonData.find(item => item.sector === "Service");
                                        if (!serviceData) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        
                                        const unionValue = serviceData?.union?.count ?? 0;
                                        const mixedValue = serviceData?.mixed?.count ?? 0;
                                        const nonUnionValue = serviceData?.nonUnion?.count ?? 0;
                                        
                                        const totalValue = unionValue + mixedValue + nonUnionValue;
                                        const serviceYAxisMax = totalValue > 0 ? Math.ceil(totalValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: ['Service'],
                                                        y: [unionValue],
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(serviceData.union?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Service'],
                                                        y: [mixedValue],
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(serviceData.mixed?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Service'],
                                                        y: [nonUnionValue],
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(serviceData.nonUnion?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 40 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, serviceYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Distribution/Pipeline */}
                        {hasData(comparisonData.find(item => item.sector === "Distribution/Pipeline")) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Distribution/Pipeline</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const distributionData = comparisonData.find(item => item.sector === "Distribution/Pipeline");
                                        if (!distributionData) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        
                                        const unionValue = distributionData?.union?.count ?? 0;
                                        const mixedValue = distributionData?.mixed?.count ?? 0;
                                        const nonUnionValue = distributionData?.nonUnion?.count ?? 0;
                                        
                                        const totalValue = unionValue + mixedValue + nonUnionValue;
                                        const distributionYAxisMax = totalValue > 0 ? Math.ceil(totalValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: ['Distribution/Pipeline'],
                                                        y: [unionValue],
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(distributionData.union?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Distribution/Pipeline'],
                                                        y: [mixedValue],
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(distributionData.mixed?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Distribution/Pipeline'],
                                                        y: [nonUnionValue],
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(distributionData.nonUnion?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 40 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, distributionYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Fabrication */}
                        {hasData(comparisonData.find(item => item.sector === "Fabrication")) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Fabrication</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const fabricationData = comparisonData.find(item => item.sector === "Fabrication");
                                        if (!fabricationData) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        
                                        const unionValue = fabricationData?.union?.count ?? 0;
                                        const mixedValue = fabricationData?.mixed?.count ?? 0;
                                        const nonUnionValue = fabricationData?.nonUnion?.count ?? 0;
                                        
                                        const totalValue = unionValue + mixedValue + nonUnionValue;
                                        const fabricationYAxisMax = totalValue > 0 ? Math.ceil(totalValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: ['Fabrication'],
                                                        y: [unionValue],
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(fabricationData.union?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Fabrication'],
                                                        y: [mixedValue],
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(fabricationData.mixed?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Fabrication'],
                                                        y: [nonUnionValue],
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(fabricationData.nonUnion?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 40 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, fabricationYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Others */}
                        {hasData(comparisonData.find(item => item.sector === "Other")) && (
                            <div className="space-y-2">
                                <h3 className="text-center font-semibold">Others</h3>
                                <div className="h-[300px] min-h-[300px]">
                                    {isLoadingComparison ? (
                                        <ComparisonChartSkeleton />
                                    ) : (() => {
                                        const otherData = comparisonData.find(item => item.sector === "Other");
                                        if (!otherData) {
                                            return (
                                                <div className="h-full flex items-center justify-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-1"><svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><p className="text-sm font-medium text-gray-500">No data available</p><p className="text-xs text-gray-400">Try selecting a different time range</p></div>
                                                </div>
                                            );
                                        }
                                        
                                        const unionValue = otherData?.union?.count ?? 0;
                                        const mixedValue = otherData?.mixed?.count ?? 0;
                                        const nonUnionValue = otherData?.nonUnion?.count ?? 0;
                                        
                                        const totalValue = unionValue + mixedValue + nonUnionValue;
                                        const otherYAxisMax = totalValue > 0 ? Math.ceil(totalValue * 1.2) : 5;
                                        
                                        return (
                                            <Plot
                                                data={[
                                                    {
                                                        x: ['Others'],
                                                        y: [unionValue],
                                                        name: 'Union',
                                                        type: 'bar',
                                                        marker: { color: '#A42823' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(otherData.union?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Others'],
                                                        y: [mixedValue],
                                                        name: 'Mixed',
                                                        type: 'bar',
                                                        marker: { color: '#541513' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(otherData.mixed?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    },
                                                    {
                                                        x: ['Others'],
                                                        y: [nonUnionValue],
                                                        name: 'Non-Union',
                                                        type: 'bar',
                                                        marker: { color: '#767A81' },
                                                        width: 0.15,
                                                        hovertemplate: '%{y} sites (%{text}%)<extra></extra>',
                                                        text: [(otherData.nonUnion?.percentage || 0).toFixed(1)],
                                                        textposition: 'inside',
                                                        texttemplate: '%{text}%',
                                                        textfont: { color: 'white', size: 12 }
                                                    }
                                                ]}
                                                layout={{
                                                    barmode: 'stack',
                                                    showlegend: false,
                                                    margin: { l: 40, r: 10, t: 10, b: 40 },
                                                    xaxis: { tickangle: 0 },
                                                    yaxis: { title: { text: '' }, range: [0, otherYAxisMax] },
                                                    height: 300,
                                                    bargap: 0.3
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                style={{ width: '100%', height: '100%' }}
                                                useResizeHandler={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                    </div>

                    
                </div>
            </div>
        </div>
    );
};

export default Dashboard;